import { MoreIcon } from "@/assets/icons";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

import type { AnalysisReportArtifact } from "@lokasi-ai-web-sdk/agent-ui";
import dayjs from "dayjs";

type ReportExportFormat = "pdf" | "csv";

const EXPORT_FORMATS: ReportExportFormat[] = ["pdf", "csv"];

interface ReportArtifactCardProps {
  artifact: AnalysisReportArtifact;
  /**
   * Absent when the surface is not in the store — after a reload without
   * restore there is nothing to open, so the card is not clickable.
   */
  onOpen?: () => void;
  /** Absent while the caller cannot export, so the card shows no menu. */
  onExport?: (format: ReportExportFormat) => void;
}

/** The transcript card for one published report version. Props only. */
export function ReportArtifactCard({
  artifact,
  onOpen,
  onExport
}: ReportArtifactCardProps) {
  const report = artifact.report;
  const subtitle = [
    report?.version !== undefined &&
      `Version ${String(report.version).padStart(2, "0")}`,
    report?.createdAt && dayjs(report.createdAt).format("DD MMM YYYY hh:mm A")
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <div
      className={cn(
        "flex items-center gap-2 rounded-xl border border-gainsboro bg-white-smoke px-4 py-3 transition-colors",
        // Without the group class the group-hover colours below never fire, so
        // a card with nothing to open stays plain.
        onOpen && "group cursor-pointer hover:border-teal-500 hover:bg-teal-50"
      )}
    >
      <button
        type="button"
        onClick={onOpen}
        disabled={!onOpen}
        className="min-w-0 flex-1 text-left"
      >
        <p className="truncate text-sm font-semibold text-txtHeading group-hover:text-teal-500">
          {report?.title ?? "Analysis report"}
        </p>
        {subtitle && (
          <p className="mt-0.5 truncate text-xs text-txtBody group-hover:text-teal-500">
            {subtitle}
          </p>
        )}
      </button>

      {onExport && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label="Report actions"
              className="shrink-0 rounded p-1"
            >
              <MoreIcon className="size-4 rotate-90 fill-txtBody" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="min-w-[96px] p-0">
            {EXPORT_FORMATS.map((format) => (
              <DropdownMenuItem
                key={format}
                onSelect={() => onExport(format)}
                className="cursor-pointer rounded-none px-4 py-2.5 text-sm focus:bg-teal-50"
              >
                {format.toUpperCase()}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
}
